/**
 * Browser cache of photos on the 14-day SMS inbox.
 * IndexedDB database SmsInboxPhotos. Rows older than 14 days are removed.
 */

const SMS_PHOTO_CACHE_DAYS = 14;
const DB_NAME = "SmsInboxPhotos";
const STORE = "photos";
const DB_VERSION = 1;

function smsPhotoIsWithinDays(
  messageCreatedAt,
  now = new Date(),
  days = SMS_PHOTO_CACHE_DAYS,
) {
  const t = new Date(messageCreatedAt).getTime();
  const n = new Date(now).getTime();
  if (!Number.isFinite(t) || !Number.isFinite(n)) return false;
  const windowMs = Number(days) * 24 * 60 * 60 * 1000;
  return t >= n - windowMs;
}

function smsMediaMessageTimes(rows) {
  const times = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    for (const item of (row && row.media) || []) {
      const id = Number(item && item.id);
      if (id) times.set(id, row.createdAt);
    }
  }
  return times;
}

function trimSmsPhotoCacheRecords(
  records,
  now = new Date(),
  days = SMS_PHOTO_CACHE_DAYS,
) {
  const kept = [];
  const droppedIds = [];
  for (const row of Array.isArray(records) ? records : []) {
    const id = Number(row && row.id);
    if (smsPhotoIsWithinDays(row && row.messageCreatedAt, now, days)) {
      kept.push(row);
    } else if (id) {
      droppedIds.push(id);
    }
  }
  return { kept, droppedIds };
}

function idbRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function waitForTx(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
}

async function getCachedSmsPhoto(id) {
  const key = Number(id);
  if (!key) return null;
  let db;
  try {
    db = await openDb();
    const tx = db.transaction(STORE, "readonly");
    const row = await idbRequest(tx.objectStore(STORE).get(key));
    await waitForTx(tx);
    if (!row || !(row.blob instanceof Blob) || row.blob.size === 0) return null;
    if (!smsPhotoIsWithinDays(row.messageCreatedAt)) return null;
    return row.blob;
  } catch (err) {
    return null;
  } finally {
    if (db) db.close();
  }
}

async function putCachedSmsPhoto({ id, blob, messageCreatedAt, contentType } = {}) {
  const key = Number(id);
  if (!key || !(blob instanceof Blob) || blob.size === 0) return;
  if (!smsPhotoIsWithinDays(messageCreatedAt)) return;
  let db;
  try {
    db = await openDb();
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put({
      id: key,
      blob,
      contentType: contentType || blob.type || "",
      messageCreatedAt: messageCreatedAt || null,
      cachedAt: Date.now(),
    });
    await waitForTx(tx);
  } catch (err) {
    /* private mode or a full disk */
  } finally {
    if (db) db.close();
  }
}

async function trimCachedSmsPhotos(now = new Date()) {
  let db;
  try {
    db = await openDb();
    const read = db.transaction(STORE, "readonly");
    const rows = (await idbRequest(read.objectStore(STORE).getAll())) || [];
    await waitForTx(read);
    const { droppedIds } = trimSmsPhotoCacheRecords(rows, now);
    if (!droppedIds.length) return;
    const write = db.transaction(STORE, "readwrite");
    const store = write.objectStore(STORE);
    droppedIds.forEach((id) => store.delete(id));
    await waitForTx(write);
  } catch (err) {
    /* private mode */
  } finally {
    if (db) db.close();
  }
}

module.exports = {
  SMS_PHOTO_CACHE_DAYS,
  smsPhotoIsWithinDays,
  smsMediaMessageTimes,
  trimSmsPhotoCacheRecords,
  getCachedSmsPhoto,
  putCachedSmsPhoto,
  trimCachedSmsPhotos,
};
